import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task, timeout } from 'ember-concurrency';
import { guidFor } from '@ember/object/internals';
import { debug } from '@ember/debug';
import { buildWaiter } from '@ember/test-waiters';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("<div class=\'input-tel-wrap\' {{did-insert this.onInsert}} {{did-update this.onValueChange @value}}>\n  {{! `...attributes` belongs on the INPUT, not on this wrapper, and it comes\n      LAST so a host can override any attribute below (including the hooks).\n      Without it a `data-test-*` the host put on `<BbInputTel …>` reached the DOM\n      nowhere, so a carrier with two tel fields — contact-edit\'s mobile and\n      telephone — was addressable only through `input[type=\"tel\"]`, which matches\n      both: no consumer test could say which field it drove, and `@onChange` /\n      `@onValidChange` were undrivable fleet-wide.\n      The three data-test hooks name the three events that reach a callback:\n      `input` and `countrychange` both run the change path (`@onChange` +\n      `@onValidChange`), `focusout` additionally runs `@focusOutAction`. }}\n  <input\n    id={{this.inputId}}\n    type=\'tel\'\n    disabled={{@disabled}}\n    name={{@name}}\n    placeholder={{@placeholder}}\n    required={{@required}}\n    autocomplete=\'off\'\n    aria-invalid={{if (or this.errorMsg @error) \'true\'}}\n    aria-describedby={{if (or this.errorMsg @error) (concat this.inputId \'-error\')}}\n    class=\'{{if this.errorMsg \"error\"}} {{if @error \"error\"}}\'\n    data-test-input-tel\n    data-test-handle-change\n    data-test-country-change\n    data-test-focus-out\n    ...attributes\n    {{on \'input\' (perform this.handleChangeTask)}}\n    {{on \'countrychange\' (perform this.handleChangeTask)}}\n    {{on \'focusout\' this.focusOut}}\n  />\n\n  {{#if (or this.errorMsg @error)}}\n    <span id={{concat this.inputId \'-error\'}} role=\'alert\' class=\'input-definition text-alert\'>\n      {{or this.errorMsg @error}}\n    </span>\n  {{/if}}\n</div>\n");

var _dec, _dec2, _dec3, _dec4, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9;
const _telWaiter = buildWaiter('beebee-component-input:bb-input-tel');

// ─── Cache a livello di modulo: condivisa da tutte le istanze del componente ──
// Evita caricamenti duplicati e garantisce che istanze concorrenti attendano
// la stessa Promise invece di creare <link>/<script> multipli.
const _assetCache = {};
function _loadAsset(tag, attr, url) {
  if (_assetCache[url]) return _assetCache[url];
  _assetCache[url] = new Promise((resolve, reject) => {
    // Controlla se l'elemento esiste già E è stato caricato
    const existing = document.querySelector(`${tag}[${attr}="${url}"]`);
    if (existing) {
      // Risolvi subito solo se l'asset è davvero pronto: foglio di stile
      // applicato, oppure script già valutato (global disponibile). Un tag
      // <script> presente ma ancora in-flight NON ha eseguito: attendere il
      // load, altrimenti initTelTask chiamerebbe una funzione undefined.
      if (tag === 'link' && existing.sheet) {
        resolve();
        return;
      }
      if (tag === 'script' && window.intlTelInput) {
        resolve();
        return;
      }
      // Altrimenti attendi il caricamento in corso
      existing.addEventListener('load', resolve, {
        once: true
      });
      existing.addEventListener('error', () => {
        // Rimuovi dalla cache così un retry futuro può riprovare
        delete _assetCache[url];
        reject(new Error(`Failed to load ${url}`));
      }, {
        once: true
      });
      return;
    }
    const el = document.createElement(tag);
    if (tag === 'link') {
      el.rel = 'stylesheet';
      el.href = url;
    } else {
      el.src = url;
    }
    el.onload = resolve;
    el.onerror = () => {
      // Rimuovi dalla cache così un retry futuro può riprovare
      delete _assetCache[url];
      reject(new Error(`Failed to load ${url}`));
    };
    document.head.appendChild(el);
  });
  return _assetCache[url];
}

// intl-tel-input publishes its own readiness signal (`instance.promise`, which
// settles once utils.js has landed and the initial country is resolved). Await
// THAT rather than guessing with a timeout — but bound the wait, because the
// library has a state in which the signal never settles: an instance created
// after a previous utils.js load has already started gets `loadUtils() === null`
// and, if that earlier load failed, no `handleUtils` broadcast ever reaches it.
// Waiting forever there would hang every consumer's test suite and leave the
// field permanently uninitialised in production. A timed-out wait is treated
// exactly like a failed utils load, which `_currentNumber` and
// `_basicPhoneValidation` already cover.
const WIDGET_READY_TIMEOUT_MS = 4000;

// Same reasoning for the ip-location lookup, which is a THIRD-PARTY, rate-limited
// endpoint: an unanswered request would previously leave the widget without a
// country indefinitely, and now — inside the waiter — would hold `settled()` in
// every consumer suite that renders a phone field. Abort it and fall back to the
// default country, which is what the existing catch already does.
const IP_LOOKUP_TIMEOUT_MS = 3000;
function _abortAfter(ms) {
  // `AbortSignal.timeout` is not in every browser this fleet supports; without it
  // the request simply keeps its previous unbounded behaviour.
  return typeof AbortSignal?.timeout === 'function' ? {
    signal: AbortSignal.timeout(ms)
  } : {};
}
function _widgetReady(intlTel) {
  const ready = intlTel?.promise;
  if (typeof ready?.then !== 'function') return Promise.resolve();
  return new Promise(resolve => {
    const timer = setTimeout(resolve, WIDGET_READY_TIMEOUT_MS);
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    // Never rejects: a broken utils.js is a degraded widget, not an error.
    ready.then(done, done);
  });
}
let BbInputTelComponent = (_dec = task(function* () {
  const waiterToken = _telWaiter.beginAsync();
  try {
    const cssUrl = `${this.pluginUrl}/css/intlTelInput.css`;
    const jsUrl = `${this.pluginUrl}/js/intlTelInput.min.js`;
    yield _loadAsset('link', 'href', cssUrl);
    if (this.isDestroying || this.isDestroyed) return;
    if (typeof window.intlTelInput !== 'function') {
      yield _loadAsset('script', 'src', jsUrl);
      if (this.isDestroying || this.isDestroyed) return;
    }
    yield this.initTelTask.perform();
  } catch {
    if (this.isDestroying || this.isDestroyed) return;
    this.errorMsg = this.intl.t('comunication error');
  } finally {
    // `finally` (not the tail of `try`) so a dropped/cancelled perform, which
    // unwinds the generator without running `catch`, still ends the token.
    _telWaiter.endAsync(waiterToken);
  }
}).drop(), _dec2 = task(function* (intlTel) {
  if (!intlTel) return;

  // This task is performed fire-and-forget from initTelTask (keeping the
  // original ordering), so it carries its OWN waiter token: otherwise the
  // ip lookup and the `countrychange` it triggers would land after
  // `settled()` had already returned.
  const waiterToken = _telWaiter.beginAsync();
  try {
    let data = this.session.ipLocation;
    if (data) {
      intlTel.setCountry(data.country_code || 'it');
      return;
    }
    try {
      const response = yield fetch('https://ipapi.co/json', _abortAfter(IP_LOOKUP_TIMEOUT_MS));
      if (this.isDestroying || this.isDestroyed) return;
      // Raw fetch resolves on HTTP errors too; don't parse an error body as success.
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      data = yield response.json();
      if (this.isDestroying || this.isDestroyed) return;
      this.session.setIpLocation(data);
      intlTel.setCountry(data.country_code || 'it');
    } catch {
      if (this.isDestroying || this.isDestroyed) return;
      intlTel.setCountry('it');
    }
  } finally {
    _telWaiter.endAsync(waiterToken);
  }
}).drop(), _dec3 = task(function* () {
  let input = document.querySelector(`#${this.inputId}`);
  if (!input) {
    let retryCount = 0;
    while (!input && retryCount < 5) {
      yield timeout(100);
      if (this.isDestroying || this.isDestroyed) return;
      input = document.querySelector(`#${this.inputId}`);
      retryCount++;
    }
    if (!input) {
      debug("BbInputTelComponent: impossibile trovare l'elemento input dopo 5 tentativi.");
      return;
    }
  }
  const intlTel = window.intlTelInput(input, {
    utilsScript: `${this.pluginUrl}/js/utils.js?1695806485509`,
    // [prod-fix] — 'FIXED_LINE' rifiutava numeri mobili US e altri paesi dove il NANP
    // non distingue fisso/mobile; MOBILE è il tipo più comune per input utente
    numberType: 'MOBILE'
  });
  this.intlTel = intlTel;

  // [prod-fix] intl-tel-input downloads `utils.js` ITSELF, asynchronously, and
  // until it lands `getNumber()` returns '' rather than the digits in the field
  // (intlTelInput.js: `if (window.intlTelInputUtils) {…} return ""`). This task
  // used to wait a hardcoded 150ms and then call `_handleChange()`, which hands
  // `@onChange('')` to the host — so any form opened with a stored phone number
  // had that number ERASED on the model whenever utils.js took longer than the
  // guess. The widget publishes its own readiness signal; await that instead.
  yield _widgetReady(intlTel);
  if (this.isDestroying || this.isDestroyed) return;

  // [prod-fix] A stored number WITHOUT an international prefix carries no
  // country of its own, so `setNumber()` leaves the widget on whatever country
  // it defaulted to — the first in the list, not the user's. `getNumber()` then
  // answers that default's E.164, and the writeback SAVES it: a legacy italian
  // `3339998877` comes back as `+13339998877`, a US number, the first time
  // anyone opens the form. So set the country FIRST (Italy, see below) and only
  // then hand the digits over.
  // A value that already starts with '+' names its own country and must not be
  // overridden.
  // A stored prefix-less number is a legacy ITALIAN number (the platform's tenants are Italian; numbers saved
  // before the widget existed carry no prefix): reading it with the visitor's ip country turned «3388117832» into
  // a Danish «+45…» («too long») for staff on a foreign ip / VPN, and a valid foreign reading would have been
  // saved back. Only an EMPTY field follows the ip lookup (a convenience for typing a new number).
  const value = this.args.value;
  if (value && value !== '') {
    if (!String(value).trim().startsWith('+')) {
      intlTel.setCountry('it');
    }
    intlTel.setNumber(value);
  } else {
    this.setInitCountryTask.perform(intlTel);
  }
  yield timeout(50);
  if (this.isDestroying || this.isDestroyed) return;
  this._handleChange();
}).drop(), _dec4 = task(function* () {
  yield timeout(50);
  if (this.isDestroying || this.isDestroyed) return;
  this._handleChange();
}).restartable(), _class = class BbInputTelComponent extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "intl", _descriptor, this);
    _initializerDefineProperty(this, "session", _descriptor2, this);
    _initializerDefineProperty(this, "intlTel", _descriptor3, this);
    _initializerDefineProperty(this, "errorMsg", _descriptor4, this);
    _initializerDefineProperty(this, "isValid", _descriptor5, this);
    this.pluginUrl = 'https://beebeeassets.beebeeboard.com/assets/intl-tel-input/18.2.1/build';
    this.errorMap = ['tel.invalid_number', 'tel.invalid_country_code', 'tel.too_short', 'tel.too_long'];
    // ─── Tasks ───────────────────────────────────────────────────────────────────
    _initializerDefineProperty(this, "initTask", _descriptor6, this);
    _initializerDefineProperty(this, "setInitCountryTask", _descriptor7, this);
    _initializerDefineProperty(this, "initTelTask", _descriptor8, this);
    _initializerDefineProperty(this, "handleChangeTask", _descriptor9, this);
  }
  get inputId() {
    return `input-tel-${guidFor(this)}`;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────────

  onInsert() {
    this.initTask.perform();
  }
  willDestroy() {
    // [regression-fix] CAT-5 — duplicate super.willDestroy() call removed
    super.willDestroy(...arguments);
    this.intlTel?.destroy();
  }
  // ─── Handlers ────────────────────────────────────────────────────────────────

  // ─── Sync @value esterno → widget ───────────────────────────────────────────

  onValueChange() {
    if (!this.intlTel) return;
    const newValue = this.args.value ?? '';
    // `_currentNumber()`, not `getNumber()`: without utils.js the widget answers
    // '' for a field that is not empty, so every re-render would look like a
    // change and re-push the same value through `setNumber` + `_handleChange`.
    const current = this._currentNumber();
    if (newValue !== current) {
      this.intlTel.setNumber(newValue);
      this._handleChange();
    }
  }
  focusOut() {
    this._handleChange();
    this.args.focusOutAction?.();
  }

  // Raw text currently in the field, independent of the widget.
  _rawInputValue() {
    return document.querySelector(`#${this.inputId}`)?.value?.trim() || '';
  }

  // [prod-fix] `intlTel.getNumber()` returns '' — NOT the digits the user can see
  // — whenever intl-tel-input's `utils.js` has not loaded. Reporting that through
  // `@onChange` makes the host store an empty string over a real phone number,
  // which is the silent-loss failure this component exists to prevent. When the
  // widget says "empty" but the field is not, believe the field.
  // An actually empty field still reports '' (clearing must propagate), and a
  // widget with utils loaded is untouched: its non-empty answer always wins.
  _currentNumber() {
    const number = this.intlTel.getNumber();
    if (number) return number;
    return this._rawInputValue();
  }

  // Validazione di base quando intl-tel-input non è disponibile o utils.js non è caricato
  _basicPhoneValidation(number) {
    const cleaned = number.replace(/[\s\-().]/g, '');
    // E.164: da 7 a 15 cifre, opzionalmente precedute da +
    return /^\+?\d{7,15}$/.test(cleaned);
  }
  _handleChange() {
    this.errorMsg = null;

    // ── Fallback: intl-tel-input non caricato ──────────────────────────────────
    if (!this.intlTel) {
      const number = this._rawInputValue();
      this.args.onChange?.(number);
      if (!number) {
        this.args.onValidChange?.(false);
        return;
      }
      const isValid = this._basicPhoneValidation(number);
      this.isValid = isValid;
      this.args.onValidChange?.(isValid);
      if (!isValid) {
        this.errorMsg = this.intl.t(this.errorMap[0]);
      }
      return;
    }
    const number = this._currentNumber();

    // Notifica il parent del nuovo valore (DDAU)
    this.args.onChange?.(number);
    if (!number || number === '') {
      this.args.onValidChange?.(false);
      return;
    }
    const isValid = this.intlTel.isValidNumber();

    // ── Fallback: utils.js non caricato (isValidNumber ritorna null) ───────────
    if (isValid === null) {
      const fallbackValid = this._basicPhoneValidation(number);
      this.isValid = fallbackValid;
      this.args.onValidChange?.(fallbackValid);
      if (!fallbackValid) {
        this.errorMsg = this.intl.t(this.errorMap[0]);
      }
      return;
    }
    this.isValid = isValid;

    // Propaga lo stato di validazione al parent
    this.args.onValidChange?.(isValid);
    if (!isValid) {
      let error = this.intlTel.getValidationError();
      if (![0, 1, 2, 3].includes(error)) error = 0;
      this.errorMsg = this.intl.t(this.errorMap[error]);
    }
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "intl", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "session", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "intlTel", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "errorMsg", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "isValid", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "onInsert", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onInsert"), _class.prototype), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "initTask", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "setInitCountryTask", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "initTelTask", [_dec3], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "handleChangeTask", [_dec4], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "onValueChange", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onValueChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "focusOut", [action], Object.getOwnPropertyDescriptor(_class.prototype, "focusOut"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbInputTelComponent);

export { BbInputTelComponent as default };
//# sourceMappingURL=bb-input-tel.js.map
